import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { request } from '../../Utils/request';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import TopNavBar from '../../Components/TopNavigation/TopNavBar';
import CryptoJS from 'crypto-js';

const ResetPassword = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [form, setForm] = useState({
    newPassword: '',
    confirmPassword: '',
  });
  const [showPassword, setShowPassword] = useState({
    new: false,
    confirm: false,
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [resetToken, setResetToken] = useState<string>('');

  // Get resetToken from location state
  useEffect(() => {
    const state = location.state as { resetToken?: string; token?: string };
    if (state?.resetToken) {
      setResetToken(state.resetToken);
    } else if (state?.token) {
      setResetToken(state.token);
    } else {
      // If no resetToken in state, redirect back
      navigate('/forgot-password');
    }
  }, [location, navigate]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));
    setError(null);
    setSuccess(null);
  };

  const togglePasswordVisibility = (field: 'new' | 'confirm') => {
    setShowPassword(prev => ({
      ...prev,
      [field]: !prev[field],
    }));
  };

  const validateForm = (): string | null => {
    if (!form.newPassword.trim()) {
      return 'Please enter a new password.';
    }

    if (form.newPassword.trim().length < 6) {
      return 'Password must be at least 6 characters long.';
    }

    if (form.newPassword !== form.confirmPassword) {
      return 'New password and confirm password do not match.';
    }

    return null;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const validationError = validateForm();
    if (validationError) {
      setError(validationError);
      return;
    }

    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      const hashPassword = (password: string, uniqueKey: string): string => {
        const hash = CryptoJS.HmacSHA256(password, uniqueKey);
        return hash.toString();
      };

      const hashedPassword = hashPassword(form.newPassword.trim(), 'ifinds');

      const res = await request<any, { newPassword: string; resetToken: string }>('/auth/forgot-password/reset', {
        method: 'POST',
        body: {
          newPassword: hashedPassword,
          resetToken: resetToken,
        },
      });

      if (res.message || res.success) {
        setSuccess(res.message || 'Password reset successfully');
        // Navigate to login screen after a short delay
        setTimeout(() => {
          navigate('/login');
        }, 1500);
      }
    } catch (error: any) {
      setError(error.message || 'Failed to reset password. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <TopNavBar title="Reset Password" canGoBack={true} />
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', background: '#ffffff', minHeight: '100vh', padding: '16px' }}>
        <Box 
          style={{ width: '100%', maxWidth: 400 }}
        >
          {/* Heading */}
          <Typography variant="h5" align="center" gutterBottom sx={{ mb: 3, fontWeight: 'bold' }}>
            Create New Password
          </Typography>

          {/* Instructional Text */}
          <Typography variant="subtitle1" align="center" sx={{ mb: 3, color: 'text.secondary' }}>
            Please enter your new password below
          </Typography>

          {/* Password Reset Form */}
          <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <TextField
              name="newPassword"
              label="Create New Password"
              type={showPassword.new ? 'text' : 'password'}
              value={form.newPassword}
              onChange={handleChange}
              required
              fullWidth
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label={showPassword.new ? 'Hide password' : 'Show password'}
                      onClick={() => togglePasswordVisibility('new')}
                      edge="end"
                    >
                      {showPassword.new ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

            <TextField
              name="confirmPassword"
              label="Confirm New Password"
              type={showPassword.confirm ? 'text' : 'password'}
              value={form.confirmPassword}
              onChange={handleChange}
              required
              fullWidth
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label={showPassword.confirm ? 'Hide password' : 'Show password'}
                      onClick={() => togglePasswordVisibility('confirm')}
                      edge="end"
                    >
                      {showPassword.confirm ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

            <Button 
              type="submit" 
              variant="contained" 
              style={{ 
                marginTop: 8, 
                backgroundColor: '#FF6B35', 
                textTransform: 'none', 
                borderRadius: 8, 
                fontWeight: 600,
                padding: '12px 16px' 
              }} 
              fullWidth
              disabled={loading || !form.newPassword || !form.confirmPassword}
            >
              {loading ? 'Resetting Password...' : 'Reset Password'}
            </Button>
          </form>

          {success && <Alert severity="success" style={{ marginTop: 16 }}>{success}</Alert>}
          {error && <Alert severity="error" style={{ marginTop: 16 }}>{error}</Alert>}
        </Box>
      </div>
    </>
  );
};

export default ResetPassword;

